import { useState, useEffect, useRef, useCallback } from "react";

type CallState = "connecting" | "connected" | "missed" | "busy" | "ended";

type Props = {
  leadName: string;
  leadInitials: string;
  leadPhone: string;
  onClose: () => void;
  onEndCall: (duration: number, status: "connected" | "missed" | "busy") => void;
};

export function CallDialerPopup({ leadName, leadInitials, leadPhone, onClose, onEndCall }: Props) {
  const [callState, setCallState] = useState<CallState>("connecting");
  const [duration, setDuration] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const connectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-connect after 2 seconds
  useEffect(() => {
    if (callState === "connecting") {
      connectTimerRef.current = setTimeout(() => {
        setCallState("connected");
      }, 2000);
    }
    return () => {
      if (connectTimerRef.current) clearTimeout(connectTimerRef.current);
    };
  }, [callState]);

  // Timer for connected state
  useEffect(() => {
    if (callState === "connected") {
      timerRef.current = setInterval(() => {
        setDuration((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [callState]);

  const formatDuration = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const handleEndCall = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    onEndCall(duration, "connected");
  }, [duration, onEndCall]);

  const handleSimulateMissed = () => {
    if (connectTimerRef.current) clearTimeout(connectTimerRef.current);
    setCallState("missed");
    if (timerRef.current) clearInterval(timerRef.current);
    onEndCall(0, "missed");
  };

  const handleSimulateBusy = () => {
    if (connectTimerRef.current) clearTimeout(connectTimerRef.current);
    setCallState("busy");
    if (timerRef.current) clearInterval(timerRef.current);
    onEndCall(0, "busy");
  };

  // Connecting dots animation
  const [dots, setDots] = useState(".");
  useEffect(() => {
    if (callState !== "connecting") return;
    const int = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? "." : prev + "."));
    }, 500);
    return () => clearInterval(int);
  }, [callState]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-80 shadow-2xl p-6 text-center z-10">
        {/* Phone icon with rings */}
        <div className="relative inline-flex items-center justify-center mb-5">
          {/* Pulsing rings — only during connecting */}
          {callState === "connecting" && (
            <>
              <div className="absolute inset-0 w-20 h-20 rounded-full border-2 border-indigo-300 animate-ping opacity-0" />
              <div className="absolute inset-0 w-20 h-20 rounded-full border-2 border-indigo-300 animate-ping opacity-0" style={{ animationDelay: "0.5s" }} />
              <div className="absolute inset-0 w-20 h-20 rounded-full border-2 border-indigo-300 animate-ping opacity-0" style={{ animationDelay: "1s" }} />
            </>
          )}
          <div
            className={`w-16 h-16 rounded-full flex items-center justify-center text-3xl ${
              callState === "connected"
                ? "bg-green-100 text-green-600"
                : callState === "missed"
                ? "bg-red-100 text-red-500"
                : callState === "busy"
                ? "bg-amber-100 text-amber-600"
                : "bg-indigo-100 text-indigo-600"
            }`}
          >
            <i className="ri-phone-line w-7 h-7 flex items-center justify-center" />
          </div>
        </div>

        {/* Lead info */}
        <div className="w-[60px] h-[60px] rounded-full bg-indigo-100 text-indigo-700 text-xl font-bold flex items-center justify-center mx-auto mb-3">
          {leadInitials}
        </div>
        <p className="font-bold text-gray-900 text-lg">{leadName}</p>
        <p className="text-sm text-gray-500">{leadPhone}</p>

        {/* Status */}
        {callState === "connecting" && (
          <>
            <p className="text-indigo-600 font-medium text-sm mt-3">
              Connecting{dots}
            </p>
            <p className="text-xs text-gray-400 mt-4 cursor-pointer hover:text-gray-600 transition-colors" onClick={onClose}>
              Cancel
            </p>
            {/* Simulate buttons for testing */}
            <div className="mt-3 flex items-center justify-center gap-2">
              <button
                onClick={handleSimulateMissed}
                className="text-[10px] text-gray-400 hover:text-red-500 cursor-pointer whitespace-nowrap transition-colors"
              >
                Sim: Missed
              </button>
              <span className="text-gray-300 text-[10px]">|</span>
              <button
                onClick={handleSimulateBusy}
                className="text-[10px] text-gray-400 hover:text-amber-500 cursor-pointer whitespace-nowrap transition-colors"
              >
                Sim: Busy
              </button>
            </div>
          </>
        )}

        {callState === "connected" && (
          <>
            <p className="text-green-600 font-medium text-sm mt-3">
              <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1.5" /> Connected
            </p>
            <p className="font-mono text-2xl font-bold text-gray-900 mt-2">
              {formatDuration(duration)}
            </p>
            <div className="flex items-center justify-center gap-3 mt-5">
              <button className="w-14 h-14 rounded-xl bg-gray-100 text-gray-600 flex flex-col items-center justify-center gap-0.5 hover:bg-gray-200 transition-colors cursor-pointer">
                <i className="ri-mic-off-line text-lg w-4 h-4 flex items-center justify-center" />
                <span className="text-[10px] font-medium whitespace-nowrap">Mute</span>
              </button>
              <button className="w-14 h-14 rounded-xl bg-amber-100 text-amber-600 flex flex-col items-center justify-center gap-0.5 hover:bg-amber-200 transition-colors cursor-pointer">
                <i className="ri-pause-line text-lg w-4 h-4 flex items-center justify-center" />
                <span className="text-[10px] font-medium whitespace-nowrap">Hold</span>
              </button>
              <button
                onClick={handleEndCall}
                className="w-14 h-14 rounded-xl bg-red-500 text-white flex flex-col items-center justify-center gap-0.5 hover:bg-red-600 transition-colors cursor-pointer"
              >
                <i className="ri-phone-fill text-lg w-4 h-4 flex items-center justify-center rotate-[135deg]" />
                <span className="text-[10px] font-medium whitespace-nowrap">End</span>
              </button>
            </div>
          </>
        )}

        {callState === "missed" && (
          <p className="text-red-500 font-medium text-sm mt-3">
            <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-1.5" /> Missed Call
          </p>
        )}

        {callState === "busy" && (
          <p className="text-amber-600 font-medium text-sm mt-3">
            <span className="inline-block w-2 h-2 rounded-full bg-amber-500 mr-1.5" /> Busy
          </p>
        )}
      </div>
    </div>
  );
}